import { useMemo, useState } from "react";
import { theme, card, btnPrimary, inputStyle, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { RegisterToolbar } from "./RegisterToolbar";
import { DimensionHeading } from "./Drawing";
import { by, downloadCsv, matchesSearch, type SortOption } from "../lib/registerExport";

// Active assets with no network zone, and a bulk "place in zone". Segmentation cannot be assessed
// for an asset whose zone is unknown, and the rules never guess one (lib/networkZones.ts), so this
// queue is the way to close that gap. The API checks every asset id and the zone against the
// organisation (POST /estate/zones/:id/assets).

export interface EstateAsset {
  id: string; name: string; criticality: string; kind: string; status: string;
  zoneId?: string | null; purdueLevel?: number | null; safetyRelated?: boolean;
}

const CRITICALITY_ORDER = ["critical", "high", "medium", "low"] as const;
const SORTS: SortOption<EstateAsset>[] = [
  { key: "criticality", label: "Criticality", compare: by.rank<EstateAsset>((a) => a.criticality, CRITICALITY_ORDER) },
  { key: "name", label: "Name", compare: by.text<EstateAsset>((a) => a.name) },
  { key: "kind", label: "Kind", compare: by.text<EstateAsset>((a) => a.kind) },
];

const words = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

export function EstateUnassignedQueue({ assets, zones, canEdit, onPlaced }: {
  assets: EstateAsset[];
  zones: Array<{ id: string; name: string }>;
  canEdit: boolean;
  onPlaced: (message: string) => Promise<void> | void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [zoneId, setZoneId] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("criticality");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const queue = assets.filter((a) => a.status === "active" && !a.zoneId);
  const shown = useMemo(() => {
    const rows = queue.filter((a) => matchesSearch(search, a.name, a.kind, a.criticality));
    const cmp = SORTS.find((o) => o.key === sort)?.compare;
    return cmp ? [...rows].sort(cmp) : rows;
  }, [queue, search, sort]);

  if (queue.length === 0) {
    return <div style={{ ...card, ...text.hint }}>Every active asset is placed in a zone.</div>;
  }

  const allShownSelected = shown.length > 0 && shown.every((a) => selected.has(a.id));
  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function place() {
    const ids = [...selected].filter((id) => queue.some((a) => a.id === id));
    if (!zoneId || ids.length === 0) return;
    setBusy(true); setError("");
    try {
      const res = await api.post<{ placed: number }>(`/estate/zones/${zoneId}/assets`, { assetIds: ids });
      setSelected(new Set());
      await onPlaced(`${res.placed} asset${res.placed === 1 ? "" : "s"} placed in ${zones.find((z) => z.id === zoneId)?.name ?? "the zone"}.`);
    } catch (e) { setError(apiErrorMessage(e, "Could not place the assets.")); } finally { setBusy(false); }
  }

  return (
    <div style={card}>
      <DimensionHeading as="h3" count={queue.length} style={{ marginBottom: 2 }}>Assets with no zone</DimensionHeading>
      <div style={{ ...text.hint, marginBottom: 10 }}>
        Segmentation cannot be assessed for these. Select them and place them in the zone they sit in.
      </div>
      <RegisterToolbar<EstateAsset>
        search={search}
        onSearch={setSearch}
        placeholder="Search assets"
        sortOptions={SORTS}
        sort={sort}
        onSort={setSort}
        count={{ shown: shown.length, total: queue.length }}
        onExport={() => downloadCsv("assets-without-zone", [
          { header: "Asset", value: (a: EstateAsset) => a.name },
          { header: "Kind", value: (a) => words(a.kind) },
          { header: "Criticality", value: (a) => a.criticality },
          { header: "Safety related", value: (a) => !!a.safetyRelated },
        ], shown)}
      />
      {canEdit && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
          <label style={{ ...text.hint, display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={allShownSelected}
              onChange={() => setSelected(allShownSelected ? new Set() : new Set(shown.map((a) => a.id)))}
            />
            Select all shown
          </label>
          <span style={{ flex: 1 }} />
          <select value={zoneId} onChange={(e) => setZoneId(e.target.value)} aria-label="Zone to place the selected assets in" style={{ ...inputStyle, width: "auto", padding: "7px 10px", fontSize: text.hint.fontSize }}>
            <option value="">Place in zone…</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <button type="button" onClick={place} disabled={busy || !zoneId || selected.size === 0} style={{ ...btnPrimary, padding: "7px 12px", fontSize: text.hint.fontSize, opacity: busy || !zoneId || selected.size === 0 ? 0.6 : 1 }}>
            Place {selected.size > 0 ? selected.size : ""} selected
          </button>
        </div>
      )}
      {error && <div style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 8 }}>{error}</div>}
      <div style={{ display: "grid", gap: 2, maxHeight: 360, overflowY: "auto" }}>
        {shown.map((a) => (
          <label key={a.id} style={{ ...text.hint, display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", borderTop: `1px solid ${theme.color.border}`, cursor: canEdit ? "pointer" : "default" }}>
            {canEdit && <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} />}
            <span style={{ flex: 1, minWidth: 0, color: theme.color.text }}>{a.name}</span>
            <span style={{ color: theme.color.faint }}>{words(a.kind)}</span>
            <span style={{ color: theme.color.subtle, width: 60, textAlign: "right" }}>{a.criticality}</span>
            {a.safetyRelated && <span style={{ fontSize: 10, fontWeight: 700, color: theme.color.dangerText }}>Safety</span>}
          </label>
        ))}
      </div>
    </div>
  );
}
